import { IdentityProviderProtocol, ProvisioningMode, Role, SamlNameIdFormat, UnknownOrgUnitPolicy } from '@today/types'
import { z } from 'zod'

// ─── Tenant authentication surface (TO-410) ──────────────────────────────────
// `/t/{slug}/admin/authentication` and `/t/{slug}/admin/identity/providers`.
// SHAPE only: unknown properties are refused (→ 422 VALIDATION_ERROR). The
// semantic rules that answer with their own codes — the JIT role cap
// (JIT_ROLE_REJECTED), the org-unit policy coupling, the default pointer
// (IDP_DEFAULT_INVALID), the lockout guard (AUTH_CONFIG_LOCKOUT), https-only
// metadata and the mapping guard (IDP_METADATA_INVALID / IDP_MAPPING_REJECTED),
// immutable code/protocol (IDP_FIELD_IMMUTABLE) — run in user-service.

/** Inline SAML metadata cap (the service re-checks it in bytes). */
const MAX_METADATA_XML_LENGTH = 256 * 1024

/** The `metadata` object's cap, in bytes of its JSON serialization (spec Contract). */
export const MAX_AUTH_CONFIG_METADATA_BYTES = 8192

/** `code`: lowercase, digits and inner hyphens, 1–64 characters. */
const PROVIDER_CODE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/

const samlAttributeName = z.string().trim().min(1).max(256)
// Trim first, then validate: `z.url()` would run its URL check before a chained
// `.trim()`, rejecting a padded value this field has always accepted (TO-605).
const httpsUrl = z.string().trim().max(2048).check(z.url()).startsWith('https://')

const attributeMappingSchema = z
	.object({
		email: samlAttributeName,
		groups: samlAttributeName.optional(),
		// TO-63 REQ-012: the IdP attribute projected into `custom:idp_mfa`.
		mfa: samlAttributeName.optional(),
	})
	.strict()

const samlFieldsSchema = z
	.object({
		entityId: z.string().trim().min(1).max(1024),
		ssoUrl: z.string().trim().min(1).max(2048),
		sloUrl: z.string().trim().min(1).max(2048).optional(),
		certificatePem: z
			.string()
			.trim()
			.min(1)
			.max(16 * 1024),
		nameIdFormat: z.enum(SamlNameIdFormat).optional(),
	})
	.strict()

const samlSourceCount = (input: { metadataUrl?: unknown; metadataXml?: unknown; fields?: unknown }) =>
	[input.metadataUrl, input.metadataXml, input.fields].filter((value) => value !== undefined).length

const ONE_SAML_SOURCE = { message: 'Provide exactly one of metadataUrl, metadataXml or fields' }

/** A SAML provider `configuration`: exactly one metadata source plus the signing and mapping options. */
export const samlProviderConfigurationSchema = z
	.object({
		metadataUrl: z.string().trim().min(1).max(2048).optional(),
		metadataXml: z.string().min(1).max(MAX_METADATA_XML_LENGTH).optional(),
		fields: samlFieldsSchema.optional(),
		signRequests: z.boolean().optional(),
		/** Cognito always verifies the assertion signature, so only `true` is accepted. */
		requireSignedAssertions: z.literal(true).optional(),
		attributeMapping: attributeMappingSchema,
		targetGroup: z.string().trim().min(1).max(128).optional(),
	})
	.strict()
	.refine((input) => samlSourceCount(input) === 1, ONE_SAML_SOURCE)

const oidcConfigurationShape = {
	issuer: httpsUrl,
	clientId: z.string().min(1).max(255),
	/** Write-only: stored in Secrets Manager, never returned. */
	clientSecret: z.string().min(1).max(1024),
	authorizationEndpoint: httpsUrl.optional(),
	tokenEndpoint: httpsUrl.optional(),
	userinfoEndpoint: httpsUrl.optional(),
	jwksUri: httpsUrl.optional(),
	scopes: z
		.array(z.string().min(1).max(128))
		.min(1)
		.max(20)
		.refine((scopes) => scopes.includes('openid'), { message: 'scopes must include "openid"' }),
	attributeMapping: attributeMappingSchema,
}

/** An OIDC provider `configuration` on create: the client secret is required. */
export const oidcProviderConfigurationSchema = z.object(oidcConfigurationShape).strict()

/** An OIDC provider `configuration` on PATCH: omitting `clientSecret` keeps the stored secret. */
export const oidcProviderConfigurationPatchSchema = z
	.object({ ...oidcConfigurationShape, clientSecret: oidcConfigurationShape.clientSecret.optional() })
	.strict()

const providerCode = z.string().regex(PROVIDER_CODE)
const providerName = z.string().trim().min(1).max(100)

/** `POST /t/{slug}/admin/identity/providers` — the configuration shape follows `protocol`. */
export const identityProviderCreateSchema = z.discriminatedUnion('protocol', [
	z
		.object({
			code: providerCode,
			name: providerName,
			protocol: z.literal(IdentityProviderProtocol.SAML2),
			configuration: samlProviderConfigurationSchema,
		})
		.strict(),
	z
		.object({
			code: providerCode,
			name: providerName,
			protocol: z.literal(IdentityProviderProtocol.OIDC),
			configuration: oidcProviderConfigurationSchema,
		})
		.strict(),
])

/**
 * `PATCH /t/{slug}/admin/identity/providers/{id}`. `code` and `protocol`
 * are accepted only so the service can refuse a changed value as
 * `IDP_FIELD_IMMUTABLE`; the service checks `configuration` against the stored
 * protocol.
 */
export const identityProviderPatchSchema = z
	.object({
		code: z.string().max(64).optional(),
		protocol: z.enum(IdentityProviderProtocol).optional(),
		name: providerName.optional(),
		enabled: z.boolean().optional(),
		configuration: z.union([samlProviderConfigurationSchema, oidcProviderConfigurationPatchSchema]).optional(),
	})
	.strict()

/** `POST …/identity/providers/{id}/test` — the test-login popup's code + PKCE verifier. */
export const identityProviderTestSchema = z
	.object({
		code: z.string().min(1).max(2048),
		codeVerifier: z.string().min(1).max(128),
		redirectUri: z.url().max(2048),
	})
	.strict()

/** `POST …/identity/providers/{id}/rotate-certificate` — exactly one new source. */
export const identityProviderRotateSchema = z
	.object({
		metadataUrl: z.string().trim().min(1).max(2048).optional(),
		metadataXml: z.string().min(1).max(MAX_METADATA_XML_LENGTH).optional(),
		certificatePem: z
			.string()
			.trim()
			.min(1)
			.max(16 * 1024)
			.optional(),
	})
	.strict()
	.refine(
		(input) => [input.metadataUrl, input.metadataXml, input.certificatePem].filter((v) => v !== undefined).length === 1,
		{ message: 'Provide exactly one of metadataUrl, metadataXml or certificatePem' },
	)

type OrgUnitFields = { unknownOrgUnitPolicy: UnknownOrgUnitPolicy; defaultOrgUnitId: string | null }

/** `defaultOrgUnitId` is required with `DEFAULT_ORG_UNIT` and must be null with any other policy (REQ-014). */
const orgUnitCouplingIssue = (input: OrgUnitFields): string | null => {
	const wantsOrgUnit = input.unknownOrgUnitPolicy === UnknownOrgUnitPolicy.DEFAULT_ORG_UNIT
	if (wantsOrgUnit && input.defaultOrgUnitId === null) {
		return 'defaultOrgUnitId is required when unknownOrgUnitPolicy is DEFAULT_ORG_UNIT'
	}
	if (!wantsOrgUnit && input.defaultOrgUnitId !== null) {
		return `defaultOrgUnitId must be null when unknownOrgUnitPolicy is ${input.unknownOrgUnitPolicy}`
	}
	return null
}

/** UTF-8 byte length; `TextEncoder` so the schema also runs in the SPA. */
const metadataBytes = (metadata: Record<string, unknown>): number =>
	new TextEncoder().encode(JSON.stringify(metadata)).length

/**
 * `PUT /t/{slug}/admin/authentication` — a full `AuthenticationConfig` without
 * `updatedAt` / `updatedBy`; every field is required. `defaultRole` accepts any
 * `Role` here so the service can refuse `ORG_ADMIN` and above as
 * `JIT_ROLE_REJECTED` rather than a generic shape error. Also enforces the
 * REQ-014 data rules: `metadata` is a JSON object of at most
 * {@link MAX_AUTH_CONFIG_METADATA_BYTES} serialized bytes, and the org-unit
 * policy / `defaultOrgUnitId` coupling.
 */
export const tenantAuthConfigPutSchema = z
	.object({
		loginEnabled: z.boolean(),
		localLoginEnabled: z.boolean(),
		// TO-63 REQ-010: optional; omitted keeps the stored value.
		requireIdpMfa: z.boolean().optional(),
		// TO-593 REQ-002: optional; omitted keeps the stored value.
		requireMfa: z.boolean().optional(),
		defaultProviderId: z.uuid().nullable(),
		provisioningMode: z.enum(ProvisioningMode),
		// Raw entries are bounded loosely here; the ≤ 50 rule applies after de-duplication.
		allowedEmailDomains: z.array(z.string().max(512)).max(500),
		defaultRole: z.enum(Role).nullable(),
		unknownOrgUnitPolicy: z.enum(UnknownOrgUnitPolicy),
		defaultOrgUnitId: z.uuid().nullable(),
		metadata: z.record(z.string(), z.unknown()),
	})
	.strict()
	.superRefine((input, ctx) => {
		const coupling = orgUnitCouplingIssue(input)
		if (coupling) ctx.addIssue({ code: 'custom', path: ['defaultOrgUnitId'], message: coupling })
		if (metadataBytes(input.metadata) > MAX_AUTH_CONFIG_METADATA_BYTES) {
			ctx.addIssue({
				code: 'custom',
				path: ['metadata'],
				message: `metadata must serialize to at most ${MAX_AUTH_CONFIG_METADATA_BYTES} bytes`,
			})
		}
	})

export type SamlProviderConfigurationInput = z.infer<typeof samlProviderConfigurationSchema>
export type OidcProviderConfigurationInput = z.infer<typeof oidcProviderConfigurationSchema>
export type OidcProviderConfigurationPatchInput = z.infer<typeof oidcProviderConfigurationPatchSchema>
export type IdentityProviderCreateInput = z.infer<typeof identityProviderCreateSchema>
export type IdentityProviderPatchInput = z.infer<typeof identityProviderPatchSchema>
export type IdentityProviderTestInput = z.infer<typeof identityProviderTestSchema>
export type IdentityProviderRotateInput = z.infer<typeof identityProviderRotateSchema>
export type TenantAuthConfigPutInput = z.infer<typeof tenantAuthConfigPutSchema>
